import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Grid, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import SearchSection from "./SearchSection";
import StatusBanner from "./StatusBanner";
import OrderProgressStepper from "./OrderProgressStepper";
import OrderDetailsSection from "./OrderDetailsSection";
import CustomerDetailsSection from "./CustomerDetailsSection";
import StatusHistorySection from "./StatusHistorySection";
import ScrollToTopButton from "./ScrollToTopButton";
import { fetchOrderDetails, formatDate } from "./utils";

const TrackOrder = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();
  const showBackButton = location.state?.fromModal === true;

  const handleBack = () => {
    navigate("/delivery/orders", {
      state: { reopenModal: true, jobId: location.state.jobId },
    });
  };

  // Handle URL params for auto-search
  useEffect(() => {
    const orderIdFromUrl = searchParams.get("orderId");
    if (orderIdFromUrl) {
      setOrderId(orderIdFromUrl);
      setTimeout(() => {
        handleSearchWithId(orderIdFromUrl);
      }, 100);
    }
  }, [searchParams]);

  // Handle scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchWithId = async (searchOrderId) => {
    setError("");
    setSearched(true);

    if (!searchOrderId.trim()) {
      setError("Please enter an Order No.");
      setOrderData(null);
      return;
    }

    setLoading(true);

    try {
      const result = await fetchOrderDetails(searchOrderId);

      if (result.success) {
        setOrderData(result.data);
        console.log("this is order data",orderData)
      } else {
        setOrderData(null);
        setError(result.error);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else if (error.response?.status === 404) {
        setOrderData(null);
        setError(
          error.response?.data.message ||
            "Order not found. Please check the Order No. and try again."
        );
      } else {
        handleApiError(error);
        setOrderData(null);
        setError("Failed to fetch order details. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    await handleSearchWithId(orderId);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f4f6f8",
        p: { xs: 1, sm: 2, md: 3 },
        minHeight: "100vh",
      }}
    >
      <Box sx={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{ color: "primary.main" }}
          >
            Track Your Order Status
          </Typography>
          {showBackButton && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Back
            </Button>
          )}
        </Box>

        {/* Search Section */}
        <SearchSection
          orderId={orderId}
          setOrderId={setOrderId}
          handleSearch={handleSearch}
          handleKeyPress={handleKeyPress}
          loading={loading}
          error={error}
          searched={searched}
        />

        {/* Order Details Section */}
        {orderData && (
          <Box mt={4}>
            {/* Status Banner */}
            <StatusBanner
              orderId={orderData.orderId}
              currentStatus={orderData.currentStatus}
              deliveryTime={orderData.deliveryTime}
              invoice_number = {orderData.invoiceId}
            />

            {/* Timeline Stepper (only for standard flow) */}
            <OrderProgressStepper
              currentStatus={orderData.currentStatus}
              hasPaused={orderData.hasPaused}
              hasUnloaded={orderData.hasUnloaded}
              progressStatesWithDates={orderData.progressStatesWithDates}
              progressStates={orderData.progressStates}
            />

            <Grid container spacing={3}>
              {/* Order Details & Items */}
              <OrderDetailsSection
                orderData={orderData}
                formatDate={formatDate}
              />

              {/* Customer Details */}
              <CustomerDetailsSection orderData={orderData} />

              {/* Status History */}
              <StatusHistorySection
                statusHistory={orderData.statusHistory}
                formatDate={formatDate}
              />
            </Grid>
          </Box>
        )}

        {/* Scroll to Top Button */}
        <ScrollToTopButton show={showScrollTop} onClick={scrollToTop} />
      </Box>
    </Box>
  );
};

export default TrackOrder;
